import moment from 'moment-timezone';
import { PickingProblems } from 'src/apollo/types/globalTypes';
import { ProductsBySku } from 'src/common/types';

export const BOX_PICKING_MODE = 'box';
export const REPEATED_SCAN_WINDOW_MS = 1000;

const LIVUP_TIMEZONE = 'America/Sao_Paulo';
const BOX_BARCODE_REGEX = /^(?:[A-Z]{2})?([A-Z]\d{3})(\d{3})(\d{8})[A-Za-z0-9]{5,7}$/;
const INVERTED_CASE_BOX_BARCODE_REGEX = /^(?:[a-z]{2})?[a-z]\d{3}/;
const BOX_BATCH_KITCHEN_CODE = '2';
const ALPHABET_OFFSET = 64;

export interface BoxPickingItem {
  sku: string;
  quantity: number;
  shelfLifeDays: number | null;
}

export interface ScannedBox {
  code: string;
  sku: string;
  batch: string;
  quantity: number;
}

export interface PickedBox extends ScannedBox {
  packageId: number;
}

export interface StoredBox extends ScannedBox {
  packageId?: number;
}

export interface LastScan {
  code: string;
  at: number;
}

export interface BoxPickingSummaryItem {
  sku: string;
  orderedQuantity: number;
  pickedQuantity: number;
  missingQuantity: number;
}

export interface ScanBoxParams {
  code: string;
  items: BoxPickingItem[];
  boxes: ScannedBox[];
  rejectedBatchCodes: Set<string>;
  minShelfLifePercent: number | null;
  today: moment.Moment;
}

export interface RestoreStoredBoxesParams {
  storedBoxes: StoredBox[];
  items: BoxPickingItem[];
  rejectedBatchCodes: Set<string>;
  minShelfLifePercent: number | null;
  today: moment.Moment;
}

const invertLetterCase = (code: string) =>
  code.replace(/[a-zA-Z]/g, letter => (letter === letter.toUpperCase() ? letter.toLowerCase() : letter.toUpperCase()));

export const normalizeBoxBarcode = (code: string) =>
  INVERTED_CASE_BOX_BARCODE_REGEX.test(code) ? invertLetterCase(code) : code;

export const decodeBoxBarcode = (code: string) => {
  const match = BOX_BARCODE_REGEX.exec(code);
  if (!match) {
    return null;
  }
  const [, sku, labelQuantity, manufacturing] = match;
  const manufacturingDate = moment.tz(manufacturing, 'YYYYMMDD', true, LIVUP_TIMEZONE);
  if (!manufacturingDate.isValid()) {
    return null;
  }
  return { sku, labelQuantity: Number(labelQuantity), manufacturingDate };
};

export const getBoxExpirationDate = (manufacturingDate: moment.Moment, shelfLifeDays: number) =>
  manufacturingDate.clone().add(shelfLifeDays, 'days');

export const generateBoxBatch = (sku: string, manufacturingDate: moment.Moment, shelfLifeDays: number) => {
  const skuLetterPosition = String(sku.charCodeAt(0) - ALPHABET_OFFSET).padStart(2, '0');
  const expirationDate = getBoxExpirationDate(manufacturingDate, shelfLifeDays).format('DDMMYY');
  return `${BOX_BATCH_KITCHEN_CODE}${skuLetterPosition}${sku.slice(1, 4)}${expirationDate}`;
};

export const getPickedQuantity = (boxes: ScannedBox[], sku: string) =>
  boxes.filter(box => box.sku === sku).reduce((total, box) => total + box.quantity, 0);

export const summarizeBoxPicking = (items: BoxPickingItem[], boxes: ScannedBox[]): BoxPickingSummaryItem[] =>
  items.map(item => {
    const pickedQuantity = getPickedQuantity(boxes, item.sku);
    return {
      sku: item.sku,
      orderedQuantity: item.quantity,
      pickedQuantity,
      missingQuantity: item.quantity - pickedQuantity,
    };
  });

export const scanBox = ({ code, items, boxes, rejectedBatchCodes, minShelfLifePercent, today }: ScanBoxParams) => {
  const decodedBox = decodeBoxBarcode(code);
  if (!decodedBox) {
    return { error: 'Código de caixa inválido.' };
  }
  const item = items.find(orderItem => orderItem.sku === decodedBox.sku);
  if (!item) {
    return { error: `O produto ${decodedBox.sku} não está neste pedido.` };
  }
  const remainingQuantity = item.quantity - getPickedQuantity(boxes, item.sku);
  if (remainingQuantity <= 0) {
    return { error: `O produto ${item.sku} já foi separado por completo.` };
  }
  if (!item.shelfLifeDays) {
    return { error: `O produto ${item.sku} está sem validade no cadastro.` };
  }
  const expirationDate = getBoxExpirationDate(decodedBox.manufacturingDate, item.shelfLifeDays);
  if (expirationDate.isBefore(today, 'day')) {
    return { error: `A caixa venceu em ${expirationDate.format('DD/MM/YYYY')}.` };
  }
  const batch = generateBoxBatch(item.sku, decodedBox.manufacturingDate, item.shelfLifeDays);
  if (rejectedBatchCodes.has(batch)) {
    return { error: `O lote ${batch} está bloqueado pela Qualidade. Não envie esta caixa.` };
  }
  if (typeof minShelfLifePercent === 'number') {
    const remainingShelfLifeDays = expirationDate.diff(today.clone().startOf('day'), 'days');
    if (remainingShelfLifeDays * 100 < minShelfLifePercent * item.shelfLifeDays) {
      const remainingShelfLifePercent = Math.floor((remainingShelfLifeDays * 100) / item.shelfLifeDays);
      return {
        error: `A caixa vence em ${expirationDate.format(
          'DD/MM/YYYY'
        )} e restam ${remainingShelfLifePercent}% da validade. O cliente exige ${minShelfLifePercent}%.`,
      };
    }
  }
  if (decodedBox.labelQuantity < 1) {
    return { error: 'A etiqueta desta caixa está sem quantidade. A caixa precisa de etiqueta nova, com a quantidade.' };
  }
  if (decodedBox.labelQuantity > remainingQuantity) {
    return {
      error: `A etiqueta tem ${decodedBox.labelQuantity} unidades e faltam só ${remainingQuantity} do produto ${item.sku} no pedido. A caixa precisa de etiqueta nova, com ${remainingQuantity}.`,
    };
  }
  const box: ScannedBox = { code, sku: item.sku, batch, quantity: decodedBox.labelQuantity };
  return { box };
};

export const isRepeatedScan = (lastScan: LastScan | null, code: string, now: number) =>
  Boolean(lastScan) && lastScan.code === code && now - lastScan.at < REPEATED_SCAN_WINDOW_MS;

const isPackageId = (packageId: any) => Number.isInteger(packageId) && packageId > 0;

export const getNextPackageId = (boxes: PickedBox[]) =>
  boxes.reduce((lastPackageId, box) => Math.max(lastPackageId, box.packageId), 0) + 1;

const isSamePackageAsPrevious = (boxes: PickedBox[], index: number) =>
  index > 0 && boxes[index].packageId === boxes[index - 1].packageId;

export const getPackageNumbers = (boxes: PickedBox[]) => {
  let packageNumber = 0;
  return boxes.map((_, index) => {
    if (!isSamePackageAsPrevious(boxes, index)) {
      packageNumber += 1;
    }
    return packageNumber;
  });
};

export const canJoinPreviousPackage = (boxes: PickedBox[]) =>
  boxes.length > 1 && !isSamePackageAsPrevious(boxes, boxes.length - 1);

export const joinLastBoxToPreviousPackage = (boxes: PickedBox[]) =>
  canJoinPreviousPackage(boxes)
    ? [...boxes.slice(0, -1), { ...boxes[boxes.length - 1], packageId: boxes[boxes.length - 2].packageId }]
    : boxes;

export const restoreStoredBoxes = ({
  storedBoxes,
  items,
  rejectedBatchCodes,
  minShelfLifePercent,
  today,
}: RestoreStoredBoxesParams) => {
  let nextFreePackageId = Math.max(0, ...storedBoxes.map(box => box.packageId).filter(isPackageId)) + 1;
  const boxes: PickedBox[] = [];
  storedBoxes.forEach(storedBox => {
    const scanned = scanBox({ code: storedBox.code, items, boxes, rejectedBatchCodes, minShelfLifePercent, today });
    if (scanned.box && scanned.box.quantity === storedBox.quantity) {
      const packageId = isPackageId(storedBox.packageId) ? storedBox.packageId : nextFreePackageId++;
      boxes.push({ ...scanned.box, packageId });
    }
  });
  return { boxes, droppedCount: storedBoxes.length - boxes.length };
};

export const buildBoxPickingFinishParams = (
  items: BoxPickingItem[],
  boxes: PickedBox[],
  productsBySku: ProductsBySku
) => ({
  boxes: boxes.map(({ code, quantity }, index) =>
    isSamePackageAsPrevious(boxes, index) ? { code, quantity, samePackageAsPrevious: true } : { code, quantity }
  ),
  pickingProblems: summarizeBoxPicking(items, boxes)
    .filter(item => item.missingQuantity > 0)
    .map(item => ({
      sku: item.sku,
      name: (productsBySku && productsBySku[item.sku] && productsBySku[item.sku].name) || item.sku,
      problem: PickingProblems.MISSING_PRODUCT,
      quantity: item.missingQuantity,
    })),
});

const BOX_PICKING_STORAGE_PREFIX = 'box-picking:';
const BOX_PICKING_STORAGE_TTL_MS = 7 * 24 * 60 * 60 * 1000;

export const getBoxPickingStorageKey = (distributionCenter: string, order: string) =>
  `${BOX_PICKING_STORAGE_PREFIX}${distributionCenter}:${order}`;

const isStoredBox = (box: any, skus: Set<string>) =>
  Boolean(box) &&
  typeof box.code === 'string' &&
  typeof box.batch === 'string' &&
  skus.has(box.sku) &&
  Number.isInteger(box.quantity) &&
  box.quantity > 0;

export const loadStoredBoxes = (storageKey: string, items: BoxPickingItem[]): StoredBox[] => {
  try {
    const stored = JSON.parse(localStorage.getItem(storageKey) || 'null');
    const skus = new Set(items.map(item => item.sku));
    return stored && Array.isArray(stored.boxes) ? stored.boxes.filter(box => isStoredBox(box, skus)) : [];
  } catch (error) {
    return [];
  }
};

export const storeBoxes = (storageKey: string, boxes: StoredBox[], now = Date.now()) => {
  try {
    localStorage.setItem(storageKey, JSON.stringify({ savedAt: now, boxes }));
  } catch (error) {
    return;
  }
};

const isExpiredStoredBoxes = (storedValue: string, now: number) => {
  try {
    const stored = JSON.parse(storedValue);
    return !stored || typeof stored.savedAt !== 'number' || now - stored.savedAt > BOX_PICKING_STORAGE_TTL_MS;
  } catch (error) {
    return true;
  }
};

export const purgeExpiredStoredBoxes = (now = Date.now()) => {
  try {
    Array.from({ length: localStorage.length }, (_, index) => localStorage.key(index))
      .filter(key => key && key.startsWith(BOX_PICKING_STORAGE_PREFIX))
      .filter(key => isExpiredStoredBoxes(localStorage.getItem(key), now))
      .forEach(key => localStorage.removeItem(key));
  } catch (error) {
    return;
  }
};

export const clearStoredBoxes = (storageKey: string) => {
  try {
    localStorage.removeItem(storageKey);
  } catch (error) {
    return;
  }
};
